import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Button,
  notification,
  Row,
  Col,
  Typography,
  Checkbox,
  Space,
  message,
  Spin
} from "antd";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { usersAPI, countriesAPI } from "@api";
import dayjs from "dayjs";
import { ExclamationCircleFilled, UploadOutlined } from "@ant-design/icons"; import i18next from "i18next";
import { useNavigate, useParams } from "react-router-dom";


const { Item } = Form;
const { Title } = Typography;
const { confirm } = Modal;
export const CreateEditUsersMobile = () => {

  const { id } = useParams();
  const language = i18next.language.substring(0, 2);
  const navigate = useNavigate()
  const [file, setFile] = useState([]);
  const [form] = Form.useForm();
  const { t } = useTranslation("common");
  const { t: tError } = useTranslation("error");

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersAPI.getUser(id).then(({ data }) => data),
    onError: ({ response }) => {
      notification.error({
        message: tError(response.data.message),
      })
    },
    enabled: !!id,
  })

  const { data: countries } = useQuery({
    queryKey: ["countryOptions"],
    queryFn: () =>
      countriesAPI.getCountries().then(({ data }) =>
        data.map((country) => ({
          label: country.name[language],
          value: country.id,
        }))
      ),
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const { mutate, isLoading: confirmLoading } = useMutation({
    mutationFn: ({ id, values }) => {
      if (user) {
        return usersAPI.updateUser(id, values);
      } else {
        return usersAPI.createUser(values);
      }
    },
    onSuccess: () => {
      user ? message.success(t("meetDay.userForm.messageConfirmUpdateUser")) : message.success(t("meetDay.userForm.messageConfirmCreateUser"))
      window.scroll(0, 0)
      navigate('/users')
      form.resetFields();
    },
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const { mutate: activateUserMutation } = useMutation({
    mutationFn: usersAPI.activateUser,
    onSuccess: () => {
      window.scroll(0, 0)
      navigate('/users')
      message.success(t("meetDay.userForm.messageConfirmActivateUser"))

    },
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const { mutate: deactivateUserMutation } = useMutation({
    mutationFn: usersAPI.deactivateUser,
    onSuccess: () => {
      window.scroll(0, 0)
      navigate('/users')
      message.success(t("meetDay.userForm.messageConfirmDeactivateUser"))

    },
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });


  useEffect(() => {
    if (user)
      form.setFieldsValue({
        ...user,
        birthDate: user.birthDate ? dayjs(user.birthDate) : "",
        countryId: user.country?.id,
      });
  }, [user]);

  const showActivateUserConfirm = (user) => {
    confirm({
      title: t("userTable.enable.confirm"),
      icon: <ExclamationCircleFilled />,
      content: t("userTable.enable.description"),
      okType: "danger",
      okText: t("userTable.enable.ok"),
      cancelText: t("userTable.enable.cancel"),
      maskClosable: true,
      centered: true,
      onOk: () => activateUserMutation(user.id),
    });
  };

  const showDeactivateUserConfirm = (user) => {
    confirm({
      title: t("userTable.disable.confirm"),
      icon: <ExclamationCircleFilled />,
      content: t("userTable.disable.description"),
      okType: "danger",
      okText: t("userTable.disable.ok"),
      cancelText: t("userTable.disable.cancel"),
      maskClosable: true,
      centered: true,
      onOk: () => deactivateUserMutation(user.id),
    });
  };

  return (
    <Row style={{ background: '#fff' }}>
      <Row style={{ margin: '16px' }}>

        <Spin spinning={isLoading && !!id} size="small">
          <Form
            form={form}
            name="awardForm"
            onFinish={(values) =>
              mutate({
                id: id,
                values: {
                  ...values,
                  birthDate: values.birthDate ? values.birthDate?.format("YYYY-MM-DD") : null,
                },
              })
            }
            autoComplete="off"
            scrollToFirstError
            layout="vertical"
            requiredMark={false}
            style={{ width: '100%' }}
          >
            <Row style={{ width: '100%' }}>
              <Col span={24}>
                <Title level={4} style={{ margin: '0 0 8px 0', padding: 0 }}> {id ? 'Edit user' : 'Create user'} </Title>
              </Col>
              <Col span={24}>
                <Item
                  label={t("userTable.firstName")}
                  name="firstName"
                  rules={[{ required: true, message: t("userTable.form.firstName") }]}
                >
                  <Input style={{ width: '100%' }} placeholder={t("meetDay.userForm.firstName")} />
                </Item>
              </Col>
              <Col span={24}>
                <Item
                  label={t("userTable.lastName")}
                  name="lastName"
                  rules={[{ required: true, message: t("userTable.form.lastName") }]}
                >
                  <Input placeholder={t("meetDay.userForm.lastName")} />
                </Item>
              </Col>

              <Col span={24}>
                <Item
                  label={t("userTable.username")}
                  name="username"
                  rules={[{ required: true, message: t("userTable.form.username") }]}
                >
                  <Input placeholder={t("meetDay.userForm.userName")} />
                </Item>
              </Col>

              <Col span={24}>
                <Item
                  label={t("userTable.email")}
                  name="email"
                  rules={[
                    {
                      type: "email",
                      message: t("userTable.form.email.valid"),
                    },
                    {
                      required: true,
                      message: t("userTable.form.email.required"),
                    },
                  ]}
                >
                  <Input disabled={user} placeholder={t("meetDay.userForm.email")} />
                </Item>
              </Col>

              <Col span={24}>
                <Item label={t("userTable.birthDate")} name="birthDate">
                  <DatePicker
                    placeholder={t("meetDay.userForm.birthDate")}
                    format={["L", "DDMMYYYY", "DD/MM/YYYY", "MMDDYYYY", "MM/DD/YYYY"]}
                    style={{ width: "100%" }}
                  />
                </Item>
              </Col>

              <Col span={24}>
                <Item
                  label={t("userTable.country")}
                  name="countryId"
                  rules={[
                    {
                      required: true,
                      message: t("userTable.form.country"),
                    },
                  ]}
                >
                  <Select
                    placeholder={t("meetDay.userForm.country")}
                    showSearch
                    optionFilterProp="children"
                    allowClear
                    filterOption={(input, option) =>
                      option?.label.toLowerCase().includes(input.toLowerCase())
                    }
                    options={countries}
                  />
                </Item>
              </Col>

              <Col span={24}>
                <Item
                  label={t("userTable.imageUrl")}
                  name="attachment"
                  getValueFromEvent={({ file }) => file}
                >
                  <Upload
                    listType="picture"
                    maxCount={1}
                    accept="image/png, image/jpeg, image/jpg"
                    beforeUpload={() => false}
                    multiple={false}
                    file={file}
                    onChange={({ file }) => setFile(file)}
                    disabled={user?.externalId}
                  >
                    <Button icon={<UploadOutlined />} disabled={user?.externalId}>
                      {t("userTable.form.btnUpload")}
                    </Button>
                  </Upload>
                </Item>
              </Col>

              <Col span={24}>
                <Item
                  name="superadmin"
                  valuePropName="checked"
                >
                  <Checkbox> {t("meetDay.userForm.checkSuperAdmin")} </Checkbox>
                </Item>
              </Col>

              <Col span={24}>
                <Item
                  name={["wallet", "hasInfiniteBudget"]}
                  valuePropName="checked"
                >
                  <Checkbox>{t("meetDay.userForm.checkUnlimitedBudget")}</Checkbox>
                </Item>
              </Col>
            </Row>

            <Row style={{ width: '100%' }} >
              <Col span={24}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button style={{ width: '100%' }} type="primary" htmlType="submit" loading={confirmLoading}  >
                    {user ? t("awardTable.form.btnSaveChanges") : t("meetDay.userForm.button.createUser")}
                  </Button>
                  {user ?
                    <>
                      <Button style={{ width: '100%' }} onClick={() => navigate('/users')} loading={confirmLoading}  > {t("meetDay.userForm.button.discardChanges")} </Button>
                      {user.active ? (
                        <Button onClick={() => showDeactivateUserConfirm(user)} style={{ width: '100%' }} loading={confirmLoading} danger>
                          {t("userTable.disable.default")}
                        </Button>
                      ) : (
                        <Button onClick={() => showActivateUserConfirm(user)} style={{ width: '100%' }} loading={confirmLoading} primary>
                          {t("userTable.enable.default")}
                        </Button>
                      )}
                    </>
                    :
                    <Button style={{ width: '100%' }} onClick={() => navigate('/users')} loading={confirmLoading} > {t("meetDay.userForm.button.cancel")}  </Button>
                  }
                </Space>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Row>
    </Row>
  )
}